import { useCallback, useEffect, useRef } from 'react';

import { getComprobantes as axiosGetComprobantes } from '../../../axios/Reintegro';
import { postComprobanteGasto } from '../../../axios/Reintegro/Comprobante';
import { GET_COMPROBANTES } from '../../../axios/Utils/constants';
import { ComprobantePagoReq } from '../../../types/Reintegro/types';
import { SVF } from '../../../types/Siniestro/types';
import { convertToBlob } from '../../../utils/blob';
import { createComprobanteFormData } from '../../../utils/comprobante';
import { useFetchEntity } from '../../Utils/useEntity';

export type ComprobantesBySVFId = Record<number, any[]>;

const getComprobantesCount = async (vetFormId: string): Promise<number> => {
  const numericVetFormId = Number(vetFormId);

  if (!Number.isFinite(numericVetFormId) || numericVetFormId <= 0) return 0;

  try {
    const res = await axiosGetComprobantes(GET_COMPROBANTES, numericVetFormId);
    return Array.isArray(res?.data) ? res.data.length : 0;
  } catch {
    return 0;
  }
};

const logComprobanteFormData = (formData: FormData): void => {
  const entries = Array.from(formData.entries()).map(([key, value]) => {
    if (value instanceof File) {
      return [
        key,
        {
          name: value.name,
          type: value.type,
          size: value.size,
          lastModified: value.lastModified,
        },
      ];
    }

    return [key, value];
  });

  console.log('[Comprobante] FormData before POST', Object.fromEntries(entries));
};

export const useComprobante = () => {
  const postComprobante = useCallback(
    async (values: ComprobantePagoReq, mutate?: () => Promise<any>) => {
      try {
        const previousCount = await getComprobantesCount(values.vetFormId);

        console.log('[Comprobante] postComprobante values', {
          concepto: values.concepto,
          monto: values.monto,
          vetFormId: values.vetFormId,
          comprobante:
            values.comprobante instanceof File
              ? {
                  name: values.comprobante.name,
                  type: values.comprobante.type,
                  size: values.comprobante.size,
                  lastModified: values.comprobante.lastModified,
                }
              : values.comprobante,
        });

        const blob = await convertToBlob(values.comprobante);

        const formData = createComprobanteFormData({
          comprobante: blob,
          concepto: values.concepto,
          monto: values.monto,
          vetFormId: values.vetFormId,
        });

        logComprobanteFormData(formData);

        let response;

        try {
          response = await postComprobanteGasto(formData);
        } catch (error) {
          const currentCount = await getComprobantesCount(values.vetFormId);

          if (currentCount > previousCount) {
            response = { success: true };
          } else {
            throw error;
          }
        }

        console.log('[Comprobante] postComprobante response', response);

        await mutate?.();
        return response;
      } catch (err) {
        console.error('[Comprobante] postComprobante error', err);
        return null;
      }
    },
    [],
  );

  return {
    postComprobante,
  };
};

export const useGetComprobantesBySVFs = (svfs?: SVF[]) => {
  const shouldFetch = Boolean(svfs?.length);

  // Ref para trackear qué vetFormIds ya fueron fetcheados
  const fetchedVetFormIdsRef = useRef<Set<number>>(new Set());

  // Key basada en siniestroId (no cambia cuando se agrega un SVF)
  const siniestroId = svfs?.[0]?.siniestroId;
  const key =
    shouldFetch && siniestroId ? `${GET_COMPROBANTES}-siniestro-${siniestroId}` : null;

  // Limpiar ref cuando cambia el siniestro
  useEffect(() => {
    fetchedVetFormIdsRef.current = new Set();
  }, [siniestroId]);

  // Fetcher: solo fetchea IDs que no fueron fetcheados aún
  const fetcher = async (): Promise<ComprobantesBySVFId> => {
    if (!svfs?.length) return {};

    // Filtrar solo SVFs que no fueron fetcheados
    const svfsToFetch = svfs.filter(
      (svf) =>
        Number.isFinite(Number(svf.vetFormId))
        && Number(svf.vetFormId) > 0
        && !fetchedVetFormIdsRef.current.has(svf.vetFormId),
    );

    // Si ya tenemos todos, retornar objeto vacío (se mergeará)
    if (svfsToFetch.length === 0) return {};

    // Marcar IDs ANTES del fetch
    svfsToFetch.forEach((svf) => fetchedVetFormIdsRef.current.add(svf.vetFormId));

    const results = await Promise.allSettled(
      svfsToFetch.map((svf) =>
        axiosGetComprobantes(GET_COMPROBANTES, svf.vetFormId).then((res) => ({
          id: svf.id,
          data: Array.isArray(res?.data) ? res.data : [],
        })),
      ),
    );

    return results.reduce<ComprobantesBySVFId>((acc, result) => {
      if (result.status === 'fulfilled') {
        acc[result.value.id] = result.value.data;
      }
      return acc;
    }, {});
  };

  const { data, error, isValidating, mutate } = useFetchEntity<ComprobantesBySVFId>(
    key,
    fetcher,
    {
      swrOptions: {
        // Desactivar revalidaciones automáticas para este hook
        revalidateOnReconnect: false,
        dedupingInterval: 5000,
      },
    },
  );

  // Mutate específico: solo refetch del vetFormId indicado y mergear
  const mutateByVetFormId = useCallback(
    async (vetFormId: number) => {
      if (!Number.isFinite(Number(vetFormId)) || Number(vetFormId) <= 0) return;
      if (!svfs?.length) return;

      const svf = svfs.find((s) => s.vetFormId === vetFormId);
      if (!svf) return;

      const res = await axiosGetComprobantes(GET_COMPROBANTES, vetFormId);
      const newData = Array.isArray(res?.data) ? res.data : [];

      await mutate(
        (current) => ({
          ...current,
          [svf.id]: newData,
        }),
        false,
      );
    },
    [svfs, mutate],
  );

  return {
    comprobantesBySVFs: data ?? {},
    isValidating,
    error,
    mutate,
    mutateByVetFormId,
  };
};
